import React from "react";
import { useSelector } from "react-redux";
import { AccentInfo } from "../../../StyledComponents";
import { selectUserRole } from "../../../slices/userInfoSlice";
import DiscrepanciesDetected from "./components/DiscrepanciesDetected";
import {
  TimelineChangeList,
  TimelineChangeRow,
  TimelineContent,
  TimelineMetaList,
  TimelineMetaRow,
  TimelineNewBadge,
  TimelineSection,
  TimelineSectionTitle,
  TimelineTagList,
  TimelineTagPill,
  TimelineTitle,
  TimelineTitleRow,
} from "../TimelineLayout";

const money = (value) => `$${parseFloat(value).toLocaleString("en-US", {maximumFractionDigits: 2, minimumFractionDigits: 0})}`

const UpdateRow = ({ label, oldValue, newValue }) => {
  if (!newValue) {
    return null
  }

  return (
    <TimelineChangeRow>
      <span className="timeline-change-label">{label}</span>
      <span className="timeline-change-value">
        <span>{oldValue}</span>
        <span> to </span>
        <span>{newValue}</span>
      </span>
    </TimelineChangeRow>
  )
}

const UpdateSection = ({ title, children }) => {
  const rows = React.Children.toArray(children).filter(Boolean)

  if (rows.length === 0) {
    return null
  }

  return (
    <TimelineSection>
      <TimelineSectionTitle>{title}</TimelineSectionTitle>
      <TimelineChangeList>{rows}</TimelineChangeList>
    </TimelineSection>
  )
}

const TagSection = ({ title, tags }) => {
  if (!tags || tags.length === 0) {
    return null
  }

  return (
    <TimelineSection>
      <TimelineSectionTitle>{title}</TimelineSectionTitle>
      <TimelineTagList>
        {tags.map((mortgageTag) => (
          <TimelineTagPill key={mortgageTag.label}>{mortgageTag.label}</TimelineTagPill>
        ))}
      </TimelineTagList>
    </TimelineSection>
  )
}

const ScanSummary = ({ milestone }) => (
  <>
    {(milestone.newResolved && milestone.newResolved > 0) &&
      <TimelineMetaRow>
        <span className="timeline-meta-label">resolved:</span>
        <span className="timeline-meta-value">
          {milestone.newResolved} {milestone.newResolved === 1 ? `Discrepancy resolved` : `Discrepancies resolved`}
        </span>
      </TimelineMetaRow>
    }
    {milestone.milestone === 'Public Records Updated' && milestone.activeDiscrepancies === 0 ?
      <TimelineMetaRow>
        <span className="timeline-meta-label">status:</span>
        <span className="timeline-meta-value">
          No Discrepancies remaining
          {milestone.leadAwaitingUpdates && <span> Ready to verify</span>}
        </span>
      </TimelineMetaRow>
    : milestone.activeDiscrepancies > 0 ?
      <TimelineMetaRow>
        <span className="timeline-meta-label">active:</span>
        <span className="timeline-meta-value">
          {milestone.activeDiscrepancies} {milestone.activeDiscrepancies === 1 ? `Discrepancy ` : `Discrepancies `}
          {milestone.milestone === 'Discrepancies Detected' ? `detected` : `remaining`}
        </span>
      </TimelineMetaRow>
    : null}
  </>
)

const ScanPerformed = ({milestone}) => {
  const userRole = useSelector(selectUserRole)

  return (
    <TimelineContent>
      <TimelineTitleRow>
        <TimelineTitle>{milestone.milestone}</TimelineTitle>
        {milestone.notify && <TimelineNewBadge>new</TimelineNewBadge>}
      </TimelineTitleRow>
      {milestone.milestone === 'Record Scanned Successfuly' &&
        <AccentInfo>No discrepancies detected</AccentInfo>
      }
      <TimelineMetaList>
        <ScanSummary milestone={milestone} />
        <TimelineMetaRow>
          <span className="timeline-meta-label">date:</span>
          <span className="timeline-meta-value">{milestone.date}</span>
        </TimelineMetaRow>
        {milestone.contributor &&
          <TimelineMetaRow>
            <span className="timeline-meta-label">contributor:</span>
            <span className="timeline-meta-value">{milestone.contributor}</span>
          </TimelineMetaRow>
        }
        {userRole === 'super' &&
          <TimelineMetaRow>
            <span className="timeline-meta-label">Queries:</span>
            <span className="timeline-meta-value">{milestone.details}</span>
          </TimelineMetaRow>
        }
      </TimelineMetaList>
      {milestone.newAssessmentInformation &&
        <UpdateSection title="Updated Assessment Information">
          <UpdateRow
            label="Assessed Year"
            oldValue={milestone.newAssessmentInformation.oldAssessedYear}
            newValue={milestone.newAssessmentInformation.newAssessedYear}
          />
          <UpdateRow
            label="Total Value"
            oldValue={money(milestone.newAssessmentInformation.oldAssessedValue)}
            newValue={money(milestone.newAssessmentInformation.newAssessedValue)}
          />
          <UpdateRow
            label="Land Value"
            oldValue={money(milestone.newAssessmentInformation.oldAssessedLandValue)}
            newValue={money(milestone.newAssessmentInformation.newAssessedLandValue)}
          />
          <UpdateRow
            label="Improvements Value"
            oldValue={money(milestone.newAssessmentInformation.oldAssessedImprovementValue)}
            newValue={money(milestone.newAssessmentInformation.newAssessedImprovementValue)}
          />
        </UpdateSection>
      }
      {milestone.newTaxInformation &&
        <UpdateSection title="Updated Tax Information">
          <UpdateRow
            label="Tax Year"
            oldValue={milestone.newTaxInformation.oldTaxYear}
            newValue={milestone.newTaxInformation.newTaxYear}
          />
          <UpdateRow
            label="Tax Annual Amount"
            oldValue={money(milestone.newTaxInformation.oldTaxAnnualAmount)}
            newValue={money(milestone.newTaxInformation.newTaxAnnualAmount)}
          />
        </UpdateSection>
      }
      <TagSection
        title={milestone.newLeadTags?.length > 1 ? "New Investigation Tags" : "New Investigation Tag"}
        tags={milestone.newLeadTags}
      />
      <TagSection
        title={milestone.newMortgageTags?.length > 1 ? "New Mortgage Tags" : "New Mortgage Tag"}
        tags={milestone.newMortgageTags}
      />
      {(milestone.tier1Discrepancies || milestone.tier2Discrepancies || milestone.tier3Discrepancies) &&
        <DiscrepanciesDetected type={'discrepancy'} tier1={milestone.tier1Discrepancies} tier2={milestone.tier2Discrepancies} tier3={milestone.tier3Discrepancies} />
      }
      {(milestone.tier1Updates || milestone.tier2Updates || milestone.tier3Updates) &&
        <DiscrepanciesDetected type={'update'} tier1={milestone.tier1Updates} tier2={milestone.tier2Updates} tier3={milestone.tier3Updates} />
      }
      {(milestone.tier1Resolved || milestone.tier2Resolved || milestone.tier3Resolved) &&
        <DiscrepanciesDetected type={'resolved'} tier1={milestone.tier1Resolved} tier2={milestone.tier2Resolved} tier3={milestone.tier3Resolved} />
      }
    </TimelineContent>
  )
}

export default ScanPerformed
